import { GetOrganizationsForAdmin } from '../../../server/client/ableEdge/types';
import { createServiceError } from '../../lib/serviceErrors';
import { OrganizationId } from '../../types/idFlavors';
import { buildEnergyManagerClient } from '../apiClient';
import {
    AssignOrganizationRoleToUserArgs,
    ExternalApplication,
    GetUsersInOrganization,
    Organization,
    OrganizationSecretWithValue,
    OrganizationServiceAccountWithSecretValues,
    RemoveOrganizationUserArgs,
    RequestedOrganization,
    RequestOrganization,
    ReviewOrganization,
    RevokeOrgCredentialsFromExternalApplicationArgs,
    ShareOrgCredentialsWithExternalApplicationArgs,
    UpdateOrganization,
} from './types';

const getOrganization = async (organizationId: OrganizationId): Promise<Organization> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.get<Organization>(`/api/organizations/${organizationId}`);

    return response.data;
};

const updateOrganization = async (organization: UpdateOrganization): Promise<Organization> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.put<Organization>(`/api/organizations/${organization.id}`, organization);

    return response.data;
};

const shareOrgCredentialsWithExternalApplication = async (
    args: ShareOrgCredentialsWithExternalApplicationArgs
): Promise<void> => {
    const apiClient = buildEnergyManagerClient();

    const body = {
        organizationId: args.organizationId,
    };

    await apiClient.post<undefined>(`/api/external-applications/installer-app/share-credentials`, body);
};

const revokeOrgCredentialsFromExternalApplication = async (
    args: RevokeOrgCredentialsFromExternalApplicationArgs
): Promise<void> => {
    const apiClient = buildEnergyManagerClient();

    await apiClient.delete(`/api/external-applications/installer-app/share-credentials/${args.organizationId}`);
};

const getExternalApplication = async (): Promise<ExternalApplication> => {
    const apiClient = buildEnergyManagerClient();

    const result = await apiClient.get<ExternalApplication>(`/api/external-applications/installer-app`);

    return result.data;
};

const getUsersInOrganization = async (organizationId: OrganizationId): Promise<GetUsersInOrganization[]> => {
    const apiClient = buildEnergyManagerClient();

    const result = await apiClient.get<GetUsersInOrganization[]>(`/api/organizations/${organizationId}/users`);

    return result.data;
};

const assignOrganizationRoleToUser = async (args: AssignOrganizationRoleToUserArgs): Promise<void> => {
    try {
        const apiClient = buildEnergyManagerClient();

        const body = {
            email: args.email ?? undefined,
            userId: args.userId ?? undefined,
            role: args.role,
        };

        await apiClient.post(`/api/organizations/${args.organizationId}/users`, body);
    } catch (error) {
        throw createServiceError(error, 'assignOrganizationRoleToUser');
    }
};

const rotateOrgServiceAccountSecret = async (
    organizationId: OrganizationId,
    secretName: string
): Promise<OrganizationSecretWithValue> => {
    const apiClient = buildEnergyManagerClient();

    const result = await apiClient.post<OrganizationSecretWithValue>(
        `/api/organizations/${organizationId}/rotate-secret`,
        {
            secretName,
        }
    );

    return result.data;
};

const removeOrganizationUser = async (args: RemoveOrganizationUserArgs): Promise<void> => {
    const apiClient = buildEnergyManagerClient();

    const result = await apiClient.delete(`/api/organizations/${args.organizationId}/users/${args.userId}`);

    return result.data;
};

const requestOrganization = async (organization: RequestOrganization): Promise<RequestedOrganization> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.post<RequestedOrganization>('/api/organizations/request', organization);

    return response.data;
};

const getOrganizationsForAdmin = async (): Promise<GetOrganizationsForAdmin[]> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.get<GetOrganizationsForAdmin[]>(`/api/organizations/list/all`);

    return response.data;
};

const createOrgServiceAccount = async (
    organizationId: OrganizationId
): Promise<OrganizationServiceAccountWithSecretValues> => {
    const apiClient = buildEnergyManagerClient();

    const response = await apiClient.post<OrganizationServiceAccountWithSecretValues>(
        `/api/organizations/${organizationId}/service-accounts`
    );

    return response.data;
};

const reviewOrganization = async (args: ReviewOrganization): Promise<void> => {
    const apiClient = buildEnergyManagerClient();

    await apiClient.put(`/api/organizations/${args.orgId}/review`, {
        status: args.status,
    });
};

export const OrganizationService = {
    getOrganization,
    updateOrganization,
    shareOrgCredentialsWithExternalApplication,
    revokeOrgCredentialsFromExternalApplication,
    getExternalApplication,
    getUsersInOrganization,
    rotateOrgServiceAccountSecret,
    assignOrganizationRoleToUser,
    removeOrganizationUser,
    requestOrganization,
    getOrganizationsForAdmin,
    createOrgServiceAccount,
    reviewOrganization,
};
